import {
  useEffect,
  useRef,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from "react";
import {
  CloudOff,
  Compass,
  LockKeyhole,
  Map,
  Play,
  RotateCw,
  Sailboat,
  UserRound,
} from "lucide-react";
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";

import { AppErrorBoundary } from "./AppErrorBoundary";
import { APP_PATHS, type AppHomePath } from "./routes";
import "./App.css";

export { APP_PATHS } from "./routes";
export type { AppHomePath } from "./routes";

export interface AppShellStatus {
  profileName: string | null;
  sessionCompleted: number;
  sessionTotal: number;
  persistence: "local" | "temporary";
}

export interface AppShellSlots {
  onboarding?: ReactNode;
  map?: ReactNode;
  play?: ReactNode;
  parent?: ReactNode;
}

export interface AppShellProps {
  audioControl?: ReactNode;
  homePath?: AppHomePath;
  slots?: AppShellSlots;
  status?: Partial<AppShellStatus>;
}

export const ORIENTATION_BLOCKING_QUERY =
  "(orientation: portrait) and (max-width: 56.25rem)";

function getOrientationMediaQuery(): MediaQueryList | null {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }

  return window.matchMedia(ORIENTATION_BLOCKING_QUERY);
}

function getOrientationBlockingSnapshot(): boolean {
  return getOrientationMediaQuery()?.matches ?? false;
}

function subscribeToOrientationBlocking(
  onStoreChange: () => void,
): () => void {
  const mediaQuery = getOrientationMediaQuery();

  if (mediaQuery === null) {
    return () => undefined;
  }

  mediaQuery.addEventListener("change", onStoreChange);

  return () => mediaQuery.removeEventListener("change", onStoreChange);
}

function useOrientationBlocking(): boolean {
  return useSyncExternalStore(
    subscribeToOrientationBlocking,
    getOrientationBlockingSnapshot,
    () => false,
  );
}

const DEFAULT_STATUS: AppShellStatus = {
  profileName: null,
  sessionCompleted: 0,
  sessionTotal: 4,
  persistence: "local",
};

type AppRouteSlot = keyof AppShellSlots;

interface ResolvedStatusStripProps {
  audioControl?: ReactNode;
  status: AppShellStatus;
}

function TopStatusStrip({ audioControl, status }: ResolvedStatusStripProps) {
  const profileName = status.profileName ?? "待选择";
  const statusLabel =
    "当前游玩状态：小船员" +
    profileName +
    "，今日活动" +
    status.sessionCompleted +
    "/" +
    status.sessionTotal;

  return (
    <header className="app-shell__status-strip">
      <div className="app-shell__brand" aria-label="小小启航岛">
        <span className="app-shell__brand-mark" aria-hidden="true">
          <Sailboat size={28} strokeWidth={2.5} />
        </span>
        <strong className="app-shell__brand-name">小小启航岛</strong>
      </div>

      <div
        aria-label={statusLabel}
        aria-live="polite"
        className="app-shell__status-summary"
        role="status"
      >
        <span className="app-shell__status-item">
          <UserRound aria-hidden="true" size={22} strokeWidth={2.5} />
          <span className="app-shell__status-label">小船员</span>
          <strong className="app-shell__profile-name">{profileName}</strong>
        </span>
        <span className="app-shell__status-item">
          <Compass aria-hidden="true" size={22} strokeWidth={2.5} />
          <span className="app-shell__status-label">今日</span>
          <strong>
            {status.sessionCompleted}/{status.sessionTotal}
          </strong>
        </span>
        {status.persistence === "temporary" ? (
          <span
            aria-label="进度不会保存"
            className="app-shell__status-item app-shell__status-item--temporary"
          >
            <CloudOff aria-hidden="true" size={22} strokeWidth={2.5} />
            <strong className="app-shell__temporary-label">临时模式</strong>
          </span>
        ) : null}
      </div>

      <div className="app-shell__actions">
        {audioControl}
        <nav aria-label="主要导航" className="app-shell__navigation">
          <NavLink
            aria-label="岛屿地图"
            className={({ isActive }) =>
              "app-shell__icon-link" + (isActive ? " is-active" : "")
            }
            title="岛屿地图"
            to={APP_PATHS.map}
          >
            <Map aria-hidden="true" size={27} strokeWidth={2.5} />
          </NavLink>
          <NavLink
            aria-label="家长空间"
            className={({ isActive }) =>
              "app-shell__icon-link" + (isActive ? " is-active" : "")
            }
            title="家长空间"
            to={APP_PATHS.parent}
          >
            <LockKeyhole aria-hidden="true" size={26} strokeWidth={2.5} />
          </NavLink>
        </nav>
      </div>
    </header>
  );
}

interface OrientationNoticeProps {
  noticeRef: RefObject<HTMLElement | null>;
  visible: boolean;
}

function OrientationNotice({
  noticeRef,
  visible,
}: OrientationNoticeProps) {
  return (
    <aside
      aria-atomic="true"
      aria-label="屏幕方向提示"
      aria-live="polite"
      className="app-shell__orientation-notice"
      hidden={!visible}
      ref={noticeRef}
      role="status"
      tabIndex={-1}
    >
      <span className="app-shell__orientation-icon" aria-hidden="true">
        <RotateCw size={52} strokeWidth={2.5} />
      </span>
      <div>
        <strong>请把屏幕横过来</strong>
        <span>横屏更适合启航</span>
      </div>
    </aside>
  );
}

interface RouteSlotProps {
  children: ReactNode;
  name: AppRouteSlot;
}

function RouteSlot({ children, name }: RouteSlotProps) {
  return (
    <div
      className={"app-shell__route app-shell__route--" + name}
      data-app-slot={name}
    >
      {children}
    </div>
  );
}

interface PlaceholderViewProps {
  actionLabel: string;
  actionPath: string;
  eyebrow: string;
  icon: ReactNode;
  title: string;
}

function PlaceholderView({
  actionLabel,
  actionPath,
  eyebrow,
  icon,
  title,
}: PlaceholderViewProps) {
  return (
    <section className="app-shell__placeholder">
      <div className="app-shell__route-marker" aria-hidden="true" />
      <p className="app-shell__eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <Link className="app-shell__primary-action" to={actionPath}>
        {icon}
        {actionLabel}
      </Link>
    </section>
  );
}

function OnboardingPlaceholder() {
  return (
    <PlaceholderView
      actionLabel="去岛屿地图"
      actionPath={APP_PATHS.map}
      eyebrow="准备好，向一年级出发"
      icon={<Compass aria-hidden="true" size={24} strokeWidth={2.5} />}
      title="选择小船员"
    />
  );
}

function MapPlaceholder() {
  return (
    <PlaceholderView
      actionLabel="开始今日活动"
      actionPath={APP_PATHS.play}
      eyebrow="今日航线"
      icon={<Play aria-hidden="true" size={24} strokeWidth={2.5} />}
      title="岛屿地图"
    />
  );
}

function PlayPlaceholder() {
  return (
    <PlaceholderView
      actionLabel="返回岛屿地图"
      actionPath={APP_PATHS.map}
      eyebrow="学习活动"
      icon={<Map aria-hidden="true" size={24} strokeWidth={2.5} />}
      title="准备出发"
    />
  );
}

function ParentPlaceholder() {
  return (
    <PlaceholderView
      actionLabel="返回岛屿地图"
      actionPath={APP_PATHS.map}
      eyebrow="仅限家长"
      icon={<Map aria-hidden="true" size={24} strokeWidth={2.5} />}
      title="家长空间"
    />
  );
}

function NotFoundView() {
  return (
    <section className="app-shell__error" role="status">
      <p className="app-shell__eyebrow">航线提醒</p>
      <h1>这条航线还没有开放</h1>
      <Link className="app-shell__primary-action" to={APP_PATHS.map}>
        <Map aria-hidden="true" size={24} strokeWidth={2.5} />
        回到岛屿地图
      </Link>
    </section>
  );
}

interface ShellRoutesProps {
  homePath: AppHomePath;
  slots: AppShellSlots;
}

function ShellRoutes({ homePath, slots }: ShellRoutesProps) {
  const location = useLocation();

  return (
    <main className="app-shell__content" id="app-shell-content" tabIndex={-1}>
      <AppErrorBoundary key={location.pathname}>
        <Routes>
          <Route
            element={<Navigate replace to={homePath} />}
            path={APP_PATHS.root}
          />
          <Route
            element={
              <RouteSlot name="onboarding">
                {slots.onboarding ?? <OnboardingPlaceholder />}
              </RouteSlot>
            }
            path={APP_PATHS.onboarding}
          />
          <Route
            element={
              <RouteSlot name="map">
                {slots.map ?? <MapPlaceholder />}
              </RouteSlot>
            }
            path={APP_PATHS.map}
          />
          <Route
            element={
              <RouteSlot name="play">
                {slots.play ?? <PlayPlaceholder />}
              </RouteSlot>
            }
            path={APP_PATHS.play + "/:levelId?"}
          />
          <Route
            element={
              <RouteSlot name="parent">
                {slots.parent ?? <ParentPlaceholder />}
              </RouteSlot>
            }
            path={APP_PATHS.parent + "/*"}
          />
          <Route element={<NotFoundView />} path="*" />
        </Routes>
      </AppErrorBoundary>
    </main>
  );
}

export function AppShell({
  audioControl,
  homePath = APP_PATHS.onboarding,
  slots = {},
  status,
}: AppShellProps) {
  const resolvedStatus: AppShellStatus = { ...DEFAULT_STATUS, ...status };
  const orientationBlocking = useOrientationBlocking();
  const orientationNoticeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (orientationBlocking) {
      orientationNoticeRef.current?.focus();
    }
  }, [orientationBlocking]);

  return (
    <div
      className="app-shell"
      data-orientation-blocking={orientationBlocking || undefined}
    >
      <div
        aria-hidden={orientationBlocking || undefined}
        className="app-shell__background"
        inert={orientationBlocking || undefined}
        onFocusCapture={() => {
          if (orientationBlocking) {
            orientationNoticeRef.current?.focus();
          }
        }}
      >
        <a className="app-shell__skip-link" href="#app-shell-content">
          跳到主要内容
        </a>
        <TopStatusStrip audioControl={audioControl} status={resolvedStatus} />
        <ShellRoutes homePath={homePath} slots={slots} />
      </div>
      <OrientationNotice
        noticeRef={orientationNoticeRef}
        visible={orientationBlocking}
      />
    </div>
  );
}
